// TODO: decide where this should live once it's also used by Employer Matching...
import { useCallback } from 'react'

export interface ComboboxItem {
  value: string | number
  label: string
  isSelectable?: boolean
}

export interface UseComboboxKeyHandlersOptions<T extends ComboboxItem> {
  // Data and state
  items: T[]
  isOpen: boolean
  loading?: boolean
  highlightedIndex: number
  setHighlightedIndex: (index: number) => void
  setActiveDescendant: (id: string) => void

  // Callbacks
  onSelect: (item: T) => void
  onToggle: () => void
  onClose: () => void

  // Configuration
  idPrefix: string
  filterSelectableItems?: (item: T) => boolean

  // Refs for focus management
  listboxRef?: React.RefObject<HTMLElement>
}

export interface ComboboxKeyHandlers {
  handleTriggerKeyDown: (e: React.KeyboardEvent) => void
  handleSearchKeyDown: (e: React.KeyboardEvent) => void
  handleListboxKeyDown: (e: React.KeyboardEvent) => void
}

const useComboboxKeyHandlers = <T extends ComboboxItem>({
  items,
  isOpen,
  loading = false,
  highlightedIndex,
  setHighlightedIndex,
  setActiveDescendant,
  onSelect,
  onToggle,
  onClose,
  idPrefix,
  filterSelectableItems = (item: T) => item.isSelectable !== false,
  listboxRef,
}: UseComboboxKeyHandlersOptions<T>): ComboboxKeyHandlers => {
  // Get selectable items (filter out headers, disabled items, etc.)
  const selectableItems = items.filter(filterSelectableItems)

  // Generate unique ID for an item
  const getItemId = useCallback((item: T) => `${idPrefix}-${item.value}`, [idPrefix])

  // Update highlighted state with both index and active descendant
  const updateHighlightedOption = useCallback(
    (index: number) => {
      setHighlightedIndex(index)

      if (index >= 0 && selectableItems[index]) {
        setActiveDescendant(getItemId(selectableItems[index]))
      } else {
        setActiveDescendant('')
      }
    },
    [selectableItems, setHighlightedIndex, setActiveDescendant, getItemId],
  )

  // Navigate to next valid option
  const navigateNext = useCallback(() => {
    if (selectableItems.length === 0) return

    const nextIndex =
      highlightedIndex < selectableItems.length - 1 ? highlightedIndex + 1 : highlightedIndex

    updateHighlightedOption(nextIndex)
  }, [highlightedIndex, selectableItems.length, updateHighlightedOption])

  // Navigate to previous valid option
  const navigatePrevious = useCallback(() => {
    if (selectableItems.length === 0) return

    const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : 0
    updateHighlightedOption(prevIndex)
  }, [highlightedIndex, selectableItems.length, updateHighlightedOption])

  // Navigate to first option
  const navigateFirst = useCallback(() => {
    if (selectableItems.length === 0) return
    updateHighlightedOption(0)
  }, [selectableItems.length, updateHighlightedOption])

  // Navigate to last option
  const navigateLast = useCallback(() => {
    if (selectableItems.length === 0) return
    updateHighlightedOption(selectableItems.length - 1)
  }, [selectableItems.length, updateHighlightedOption])

  // Select currently highlighted option
  const selectHighlighted = useCallback(() => {
    if (highlightedIndex >= 0 && selectableItems[highlightedIndex]) {
      onSelect(selectableItems[highlightedIndex])
    }
  }, [highlightedIndex, selectableItems, onSelect])

  // Handle keyboard events for combobox trigger (button/input that opens dropdown)
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!loading && selectableItems.length > 0) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault()
            e.stopPropagation()

            if (isOpen && highlightedIndex >= 0) {
              navigateNext()
            } else if (isOpen) {
              navigateFirst()
            } else {
              onToggle()
            }

            break

          case 'ArrowUp':
            e.preventDefault()
            e.stopPropagation()

            if (isOpen && highlightedIndex >= 0) {
              navigatePrevious()
            } else if (isOpen) {
              navigateLast()
            } else {
              onToggle()
            }

            break

          case 'Enter':
            e.preventDefault()
            e.stopPropagation()

            if (isOpen && highlightedIndex >= 0) {
              selectHighlighted()
            } else if (!isOpen) {
              onToggle()
            }

            break

          case ' ':
            if (!isOpen) {
              e.preventDefault()
              e.stopPropagation()
              onToggle()
            }

            break

          case 'Escape':
            if (isOpen) {
              e.preventDefault()
              onClose()
            }

            break

          case 'Tab':
            if (isOpen) {
              onClose()
            }

            break
        }
      } else {
        // Basic keyboard handling when no data
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault()
            e.stopPropagation()
            onToggle()

            break

          case 'Escape':
            if (isOpen) {
              e.preventDefault()
              onClose()
            }

            break

          case 'Tab':
            if (isOpen) {
              onClose()
            }

            break
        }
      }
    },
    [
      loading,
      selectableItems.length,
      isOpen,
      highlightedIndex,
      navigateNext,
      navigatePrevious,
      navigateFirst,
      navigateLast,
      selectHighlighted,
      onToggle,
      onClose,
    ],
  )

  // Handle keyboard events for search input within dropdown
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!loading && selectableItems.length > 0) {
        switch (e.key) {
          case 'Tab':
            if (isOpen) {
              e.preventDefault()
              // Move focus to the listbox
              listboxRef?.current?.focus()
            }

            break

          case 'ArrowDown':
            e.preventDefault()
            e.stopPropagation()
            // Move focus to listbox and highlight first option
            listboxRef?.current?.focus()
            navigateFirst()

            break

          case 'ArrowUp':
            e.preventDefault()
            e.stopPropagation()
            // Move focus to listbox and highlight last option
            listboxRef?.current?.focus()
            navigateLast()

            break

          case 'Escape':
            e.preventDefault()
            onClose()

            break

          case 'Enter':
            if (highlightedIndex >= 0) {
              e.preventDefault()
              selectHighlighted()
            }

            break
        }
      } else {
        // Basic keyboard handling when no data
        switch (e.key) {
          case 'Tab':
            if (isOpen) {
              onClose()
            }

            break

          case 'Escape':
            if (isOpen) {
              e.preventDefault()
              onClose()
            }

            break
        }
      }
    },
    [
      loading,
      selectableItems.length,
      isOpen,
      highlightedIndex,
      listboxRef,
      navigateFirst,
      navigateLast,
      selectHighlighted,
      onClose,
    ],
  )

  // Handle keyboard events for listbox container
  const handleListboxKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!loading && selectableItems.length > 0) {
        switch (e.key) {
          case 'Tab':
            // Close dropdown and let tab continue to next form element
            onClose()

            break

          case 'ArrowDown':
            e.preventDefault()
            e.stopPropagation()
            navigateNext()

            break

          case 'ArrowUp':
            e.preventDefault()
            e.stopPropagation()
            navigatePrevious()

            break

          case 'Home':
            e.preventDefault()
            e.stopPropagation()
            navigateFirst()

            break

          case 'End':
            e.preventDefault()
            e.stopPropagation()
            navigateLast()

            break

          case 'Enter':
          case ' ':
            if (highlightedIndex >= 0) {
              e.preventDefault()
              selectHighlighted()
            }

            break

          case 'Escape':
            e.preventDefault()
            onClose()

            break
        }
      } else {
        switch (e.key) {
          case 'Tab':
            onClose()

            break

          case 'Escape':
            e.preventDefault()
            onClose()

            break
        }
      }
    },
    [
      loading,
      selectableItems.length,
      highlightedIndex,
      navigateNext,
      navigatePrevious,
      navigateFirst,
      navigateLast,
      selectHighlighted,
      onClose,
    ],
  )

  return {
    handleTriggerKeyDown,
    handleSearchKeyDown,
    handleListboxKeyDown,
  }
}

export default useComboboxKeyHandlers
